import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {BookOpen} from '../../icons';
import {Spinner} from '../../Spinner';
import {FACT_TYPE_MODIFIER, factTypeLabelKey} from '../memory/factConstants';
import {BOOSTER_STEP_FEED, boosterPathFor} from '../ia_booster/boosterSteps';
import {parsePanelDate, relativeTimeLabel} from './panelFormatters';
import './MainCards.scss';

const PREVIEW_FACTS = 3;

// Fecha corta de un hecho ("10/07"): al lado del texto sólo hace falta ubicar el hecho en el
// tiempo, no la fecha completa. Día/mes fijo, igual que los importes del panel van en `es-AR`
// (`panelFormatters`): el formato no cambia según quién mira.
//
// Armada a mano y NO con `toLocaleDateString(..., {day:'2-digit', month:'2-digit'})`: pedir esas
// dos partes sueltas deja que el motor elija el "mejor" patrón y el ICU de Node devuelve "10/7"
// donde el navegador devuelve "10/07". El cero a la izquierda es parte del formato, no un detalle.
function shortDate(value) {
    const date = parsePanelDate(value);
    if (!date) return null;
    const pad = (number) => String(number).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
}

/**
 * Card de la Memoria del Cliente (`panel.memory`).
 *
 * Lo que hace visible es que la IA tiene contexto: sin memoria el agente responde con métricas y
 * nada más, así que la card vacía no dice "no hay datos" — dice qué se gana cargando la primera
 * reunión.
 *
 * Tres caras según el contrato: vacía (ni hechos ni documentos), nivel TOTAL (hechos recientes con
 * su tipo) y nivel MEDIO (no vienen hechos: se muestran los documentos y la última reunión).
 */
export function MemoryCard({data, loading, clientId}) {
    const {t} = useTranslation();

    if (loading) {
        return (
            <div className="box pnl-card">
                <div className="box-loader"><Spinner color="positive small"/></div>
            </div>
        );
    }
    // Nivel OFF ⇒ `enabled: false` y la card no va (el shell ya lo gatea; el guard sostiene el
    // montaje directo desde tests).
    if (!data || data.enabled === false) return null;

    const factsCount = Number(data.facts_count) || 0;
    const docsCount = Number(data.documents_ready_count) || 0;
    const pendingFacts = Number(data.pending_facts_count) || 0;
    const facts = (data.recent_facts || []).slice(0, PREVIEW_FACTS);

    const title = t('client_panel.memory_title', 'Memoria del cliente');
    // Cargar es el acto Alimenta del AI Booster (card zcK00Emw): `/memory/setup` es la única vista de
    // la Memoria que no migró a la one-page y en la 2.1 abría la caja de la Memoria clásica.
    const loadUrl = boosterPathFor(BOOSTER_STEP_FEED, clientId);
    // "Agregar reunión" nombraba UNA de las cosas que entran a la Memoria: la pantalla de destino
    // acepta además documentos, briefs y notas sueltas. Con el label viejo, un usuario que tenía un
    // brief para cargar no encontraba dónde (feedback de Pablo sobre el mockup v2, que proponía
    // "Cargar info" — se escribe entero: "info" es de chat interno, no de producto).
    const loadInfoLabel = t('client_panel.memory_load_info', '+ Cargar información');

    // Sin hechos Y sin documentos no hay nada que resumir: la card se convierte en la invitación.
    if (factsCount === 0 && docsCount === 0) {
        return (
            <div className="box pnl-card">
                <div className="pnl-card__head">
                    <h2 className="pnl-card__title">{title}</h2>
                </div>
                <div className="pnl-cnt">
                    <div className="pnl-empty">
                        <BookOpen size={30}/>
                        <p>
                            {t('client_panel.memory_empty',
                                'Dashbo todavía no sabe nada de este cliente. Carga la reunión de kickoff y '
                                + 'arranca la memoria: tu IA va a responder con el contexto del negocio, no solo '
                                + 'con las métricas.')}
                        </p>
                        <Link className="pnl-btn pnl-btn--primary" to={loadUrl}>{loadInfoLabel}</Link>
                    </div>
                </div>
            </div>
        );
    }

    const lastMeeting = relativeTimeLabel(data.last_meeting_on, t);

    return (
        <div className="box pnl-card">
            <div className="pnl-card__head">
                <h2 className="pnl-card__title">{title}</h2>
                {factsCount > 0 && (
                    <span className="pnl-cnt">
                        {t('client_panel.memory_facts_count', {
                            count: factsCount,
                            defaultValue_one: '({{count}} hecho)',
                            defaultValue_other: '({{count}} hechos)',
                        })}
                    </span>
                )}
                <span className="pnl-card__spacer"/>
                <Link className="pnl-card__link" to={`/client/${clientId}/memory`}>
                    {t('client_panel.memory_see_all', 'Ver memoria')} &rarr;
                </Link>
            </div>

            <div className="pnl-cnt">
                {/* En nivel MEDIO los hechos no viajan (contrato): el resumen son los documentos
                    listos y cuándo fue la última reunión. Un TOTAL sin hechos recientes cae acá
                    también, que es exactamente lo que hay para contar. */}
                {facts.length > 0 ? (
                    <ul className="pnl-facts">
                        {facts.map((fact) => {
                            const date = shortDate(fact.occurred_on);
                            return (
                                <li key={fact.id} className="pnl-fact">
                                    <span className={`pnl-fact__tag ${FACT_TYPE_MODIFIER[fact.type] || ''}`}>
                                        {t(factTypeLabelKey(fact.type))}
                                    </span>
                                    <span className="pnl-fact__text">{fact.text}</span>
                                    {date && <span className="pnl-fact__date">{date}</span>}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="pnl-mem__docs">
                        {t('client_panel.memory_documents', {
                            count: docsCount,
                            defaultValue_one: '{{count}} documento',
                            defaultValue_other: '{{count}} documentos',
                        })}
                        {lastMeeting && (
                            <>
                                {' · '}
                                {t('client_panel.memory_last_meeting', 'último: {{when}}', {
                                    when: lastMeeting,
                                    interpolation: {escapeValue: false},
                                })}
                            </>
                        )}
                    </p>
                )}

                {pendingFacts > 0 && (
                    <div className="pnl-card__foot">
                        <Link className="pnl-pill" to={`/client/${clientId}/memory/facts`}>
                            {t('client_panel.memory_pending', {
                                count: pendingFacts,
                                defaultValue_one: '{{count}} por revisar',
                                defaultValue_other: '{{count}} por revisar',
                            })}
                        </Link>
                    </div>
                )}

                <div className="pnl-card__foot">
                    <Link className="pnl-btn pnl-btn--ghost pnl-btn--sm" to={loadUrl}>{loadInfoLabel}</Link>
                    <span className="pnl-hint">
                        {t('client_panel.memory_hint', 'Tu IA responde con este contexto.')}
                    </span>
                </div>
            </div>
        </div>
    );
}
